import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../../../shared/services/api.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface InstallationInstructions {
  language: string;
  ios: Array<{
    model: string | null;
    version: string;
    direct_apple_installation_url: string;
    installation_via_qr_code: {
      steps: Record<string, string>;
      qr_code_data: string;
      qr_code_url: string;
    };
    installation_manual: {
      steps: Record<string, string>;
      smdp_address_and_activation_code: string;
      smdp_address: string;
      activation_code: string;
    };
    network_setup: {
      steps: Record<string, string>;
      apn_type: string;
      apn_value: string | null;
      is_roaming: boolean;
    };
  }>;
  android: Array<{
    model: string | null;
    version: string;
    installation_via_qr_code: {
      steps: Record<string, string>;
      qr_code_data: string;
      qr_code_url: string;
    };
    installation_manual: {
      steps: Record<string, string>;
      smdp_address_and_activation_code: string;
    };
    network_setup: {
      steps: Record<string, string>;
      apn_type: string;
      apn_value: string | null;
      is_roaming: boolean;
    };
  }>;
}

export interface InstructionsResponse {
  error: boolean;
  data: {
    data: {
      instructions: InstallationInstructions;
    };
  };
  message: string;
}

@Component({
  standalone: false,
  selector: 'app-sim-instructions',
  templateUrl: './sim-instructions.component.html',
  styleUrl: './sim-instructions.component.scss'
})
export class SimInstructionsComponent implements OnInit {
  instructions: InstallationInstructions | null = null;
  loading = true;
  error: string | null = null;
  expandedIosSections: boolean[] = [];
  expandedAndroidSections: boolean[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    const iccid = this.route.snapshot.paramMap.get('iccid');
    if (iccid) {
      this.loadInstructions(iccid);
    } else {
      this.error = 'SIM ICCID not found';
      this.loading = false;
    }
  }

  loadInstructions(iccid: string): void {
    // The JWT interceptor will automatically add the token header
    this.apiService.getData(`/api/airalo/sims/${iccid}/instructions`).subscribe({
      next: (response: InstructionsResponse) => {
        if (!response.error && response.data && response.data.data && response.data.data.instructions) {
          this.instructions = response.data.data.instructions;
          // Initialize expanded sections - all sections expanded by default
          if (this.instructions.ios) {
            this.expandedIosSections = this.instructions.ios.map(() => true);
          }
          if (this.instructions.android) {
            this.expandedAndroidSections = this.instructions.android.map(() => true);
          }
        } else {
          this.error = 'Failed to load instructions';
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching instructions:', err);
        this.error = 'Error loading instructions';
        this.loading = false;
      }
    });
  }

  goBack(): void {
    const iccid = this.route.snapshot.paramMap.get('iccid');
    if (iccid) {
      this.router.navigate(['/app/sims', iccid]);
    } else {
      this.router.navigate(['/app/sims']);
    }
  }

  getStepKeys(steps: Record<string, string>): string[] {
    return Object.keys(steps).sort((a, b) => parseInt(a) - parseInt(b));
  }

  toggleIosSection(index: number): void {
    // Initialize the array if it's not already initialized
    if (!this.expandedIosSections[index]) {
      this.expandedIosSections = this.instructions?.ios?.map((_, i) => i === index) || [];
    } else {
      this.expandedIosSections[index] = !this.expandedIosSections[index];
    }
  }

  toggleAndroidSection(index: number): void {
    // Initialize the array if it's not already initialized
    if (!this.expandedAndroidSections[index]) {
      this.expandedAndroidSections = this.instructions?.android?.map((_, i) => i === index) || [];
    } else {
      this.expandedAndroidSections[index] = !this.expandedAndroidSections[index];
    }
  }
}